"use client";

import React, { useState } from "react";
import TrackedLink from "@/components/custom/analytics/TrackedLink";
import { getBlogList as getClientBlogList } from "@/lib/api/client/blog";
import { useTranslations } from "next-intl";
import Image from "next/image";
import Pagination from "@/components/custom/Pagination";
import { MixpanelEvent } from "@/constant/mixpanelEvent";

interface BlogItem {
  title: string;
  description: string;
  date: string;
  image: string;
  href: string;
  blogId?: string;
  author?: string;
  status?: string;
  publishTimestamp?: number;
}

interface BlogByAlphabetListProps {
  slug: string;
  initialBlogList: BlogItem[];
  totalPages: number;
  currentPage: number;
  pageSize: number;
  isOtherAlphabet: boolean;
}
export default function BlogByAlphabetList(props: BlogByAlphabetListProps) {
  const t = useTranslations();
  const {
    slug,
    initialBlogList,
    totalPages,
    currentPage = 1,
    pageSize,
    isOtherAlphabet,
  } = props;
  const [blogList, setBlogList] = useState<BlogItem[]>(initialBlogList);
  const [page, setPage] = useState(currentPage);
  const [loading, setLoading] = useState(false);

  const loadPage = async (pageNum: number) => {
    setLoading(true);
    try {
      const params: BLOG_API.GetBlogListRequest = {
        page: pageNum,
        page_size: pageSize,
      };

      if (isOtherAlphabet) {
        params.other = true;
      } else {
        params.title_first_letter = slug;
      }
      const blogListResponse = await getClientBlogList(params);

      if (
        blogListResponse &&
        blogListResponse.metadata &&
        blogListResponse.metadata.blog_list
      ) {
        const blogs = blogListResponse.metadata.blog_list.map((item) => {
          return {
            title: item.content?.title ?? "",
            description: item.content?.description ?? "",
            date: item.publish_date,
            image: item.cover_url,
            href: `/blog/${item.url_blog_name}`,
          };
        });
        setBlogList(blogs);
        setPage(pageNum);
      }
    } catch (error) {
      console.error("Error fetching blog list from API:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {blogList.length === 0 ? (
        <div className="min-h-[740px] flex items-center justify-center">
          <div className="flex flex-col items-center justify-center">
            <Image
              src="/assets/image/share/share-invalid.webp"
              alt="No related blog available"
              width={132}
              height={132}
              sizes="132px"
              className="my-0 w-[132px] rounded-none"
            />
            <p className="text-[16px] md:text-[20px] text-[#ABAAA6] font-serif">
              {t("blog.noRelatedBlogAvailable")}
            </p>
          </div>
        </div>
      ) : (
        <div className="min-h-[740px] pt-[48px]">
          <div className="grid grid-cols-1 gap-y-[32px] items-start">
            {blogList.map((blog) => {
              // 从href中提取博客ID，优先使用blogId字段
              const blogId =
                blog.blogId || blog.href.replace("/blog/", "").replace("/", "");

              return (
                <TrackedLink
                  href={blog.href}
                  key={blogId}
                  className="group gap-[24px] self-start"
                  mixpanelEvent={MixpanelEvent.ClickBlog}
                  mixpanelEventData={{ blog_id: blogId }}
                  useIntlLink={true}
                >
                  <div className="flex gap-[24px] items-center">
                    <div className="md:w-[240px] md:h-[120px] w-[140px] h-[100px] overflow-hidden rounded-[12px] flex-shrink-0">
                      <Image
                        src={blog.image}
                        alt={blog.title}
                        width={480}
                        height={240}
                        quality={80}
                        sizes="(min-width: 768px) 240px, 140px"
                        className="my-0 h-full w-full rounded-none object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                    </div>
                    <div className="flex flex-col gap-[12px]">
                      {/* 超过两行显示 ... */}
                      <h3
                        className="md:text-[24px] text-[16px] font-semibold text-[#161615] transition-colors"
                        style={{
                          lineHeight: "normal",
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {blog.title}
                      </h3>
                      <div className="md:text-[16px] text-[14px] text-[#ABAAA6]">
                        {blog.date}
                      </div>
                    </div>
                  </div>
                </TrackedLink>
              );
            })}
          </div>
          {/* Pagination */}
          {totalPages <= 1 ? (
            <div className="flex justify-center items-center mt-[32px] md:mb-[72px] sm:mb-[0px]">
              <div className="flex-1 max-w-[120px] h-[1px] bg-[#DAD8D3] rounded-full"></div>
              <div className="px-[24px] flex-shrink-0">
                <p className="text-[#ABAAA6] text-[16px] font-[400] whitespace-nowrap">
                  {t("blog.noMorePosts")}
                </p>
              </div>
              <div className="flex-1 max-w-[120px] h-[1px] bg-[#DAD8D3] rounded-full"></div>
            </div>
          ) : (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={loadPage}
              loading={loading}
            />
          )}
        </div>
      )}
    </>
  );
}
